import React from 'react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

import { useTranslation } from 'language/translator'

import {
  BANNER_HEIGHT,
  HEADER_FONT,
  LEVEL_STYLES,
  StatusBannerItem
} from './lib'

interface ExpandedListProps {
  banners: StatusBannerItem[]
  onCollapse: () => void
  onClose: (id: string) => void
}

const ExpandedList = (props: ExpandedListProps) => {
  const { banners, onCollapse, onClose } = props
  const { t } = useTranslation()

  return (
    <Box sx={{ position: 'relative', height: BANNER_HEIGHT }}>
      <Box
        sx={{
          height: BANNER_HEIGHT,
          bgcolor: 'black.main',
          color: 'white.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2
        }}
      >
        <Box
          sx={{
            flex: 'none',
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon
            name="FormatListBulletedOutlined"
            sx={{ fontSize: 16, color: 'white.main' }}
          />
        </Box>
        <Text
          component="span"
          sx={{
            fontFamily: HEADER_FONT,
            fontWeight: 600,
            fontSize: 14,
            color: 'white.main'
          }}
        >
          {t('status_banner', 'system_messages', false, {
            count: String(banners.length)
          })}
        </Text>
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          onClick={onCollapse}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: 'rgba(255, 255, 255, 0.12)',
            color: 'white.main',
            border: 'none',
            borderRadius: 9999,
            px: 1.5,
            py: 0.625,
            fontFamily: HEADER_FONT,
            fontWeight: 600,
            fontSize: 13,
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
        >
          {t('status_banner', 'collapse')}
          <Icon name="ExpandLess" sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: BANNER_HEIGHT,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          boxShadow: '0 8px 24px rgba(26, 26, 29, 0.08)'
        }}
      >
        {banners.map((banner, index) => {
          const styles = LEVEL_STYLES[banner.level]

          return (
            <Box
              key={banner.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                px: 2.25,
                py: 1.75,
                borderTop: index > 0 ? '1px solid' : 'none',
                borderColor: 'grey.50'
              }}
            >
              <Box
                sx={{
                  flex: 'none',
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: styles.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon name="Info" sx={{ fontSize: 18, color: styles.icon }} />
              </Box>

              <Box sx={{ minWidth: 0, lineHeight: 1.4 }}>
                <Box
                  component="span"
                  sx={{
                    fontFamily: HEADER_FONT,
                    fontWeight: 700,
                    fontSize: 10.5,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color:
                      banner.level === 'important' ? 'white.main' : 'info.dark',
                    bgcolor: styles.bg,
                    borderRadius: 9999,
                    px: 1,
                    py: 0.25
                  }}
                >
                  {t(
                    'status_banner',
                    banner.level === 'important'
                      ? 'level_important'
                      : 'level_info'
                  )}
                </Box>
                <Text
                  noWrap
                  sx={{
                    fontFamily: HEADER_FONT,
                    fontWeight: 600,
                    fontSize: 15,
                    color: 'black.main',
                    mt: 0.625
                  }}
                >
                  {banner.title}
                </Text>
                <Text
                  noWrap
                  sx={{ fontSize: 14, color: 'black.main', opacity: 0.65 }}
                >
                  {banner.description}
                </Text>
              </Box>

              <Box sx={{ flex: 1 }} />

              {banner.href ? (
                <Link
                  href={banner.href}
                  newTab
                  target="_blank"
                  rel="noreferrer"
                  underline="none"
                  sx={{
                    color: 'black.main',
                    fontFamily: HEADER_FONT,
                    fontWeight: 600,
                    fontSize: 14,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {banner.linkLabel || t('status_banner', 'read_more')}
                </Link>
              ) : null}

              <IconButton
                onClick={() => onClose(banner.id)}
                size="small"
                sx={{ color: 'action.active' }}
              >
                <Icon name="Close" sx={{ fontSize: 17, color: 'inherit' }} />
              </IconButton>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default ExpandedList
